/*
 * Copyright (c) Nexign, JSC, 2019-2022.
 */

/**
 *
 *
 * @author Yakov Gushchin
 * @author Anton Panasenko
 * @version 1.19.0
 * @since 1.1.0
 */

import { TableNameType } from '../../../components/Table/types/tableTypes';
import { EOnboardingComponentsId } from '../../../modules/onboarding/types/onboardingTypes';
import { RoutePath } from '../../../routes/constants/routesConstants';
import { EExpensesAnalyzerChart } from '../components/ExpensesAnalyzerController/expensesAnalyzerControllerTypes';
import { EExpensesAnalyzerField } from '../components/ExpensesAnalyzerTable/types/expensesAnalyzerTableTypes';
import { EExpensesAnalyzerTypes, IExpensesAnalyzerMeta } from './pageExpensesAnalyzerTypes';

export const MapExpensesAnalyzerTypeToMeta: { [key in EExpensesAnalyzerTypes]: IExpensesAnalyzerMeta } = {
    [EExpensesAnalyzerTypes.Account]: {
        chartIdField: EExpensesAnalyzerField.ACCOUNT,
        isTableAccount: true,
    },
    [EExpensesAnalyzerTypes.AccountSubscribers]: {
        chartIdField: EExpensesAnalyzerField.MSISDN,
    },
    [EExpensesAnalyzerTypes.AccountSingle]: {
        chartIdField: EExpensesAnalyzerField.ACCOUNT,
        isTableAccount: true,
    },
    [EExpensesAnalyzerTypes.AccountSingleSubscribers]: {
        chartIdField: EExpensesAnalyzerField.MSISDN,
    },
    [EExpensesAnalyzerTypes.BillsAccount]: {
        chartIdField: EExpensesAnalyzerField.ACCOUNT,
        isBillsType: true,
        isDynamicDisabled: true,
        isTableAccount: true,
    },
    [EExpensesAnalyzerTypes.BillsAccountSingle]: {
        chartIdField: EExpensesAnalyzerField.ACCOUNT,
        isBillsType: true,
        isDynamicDisabled: true,
        isTableAccount: true,
    },
    [EExpensesAnalyzerTypes.BillsAccountsSubscribers]: {
        chartIdField: EExpensesAnalyzerField.MSISDN,
        isBillsType: true,
        isDynamicDisabled: true,
    },
    [EExpensesAnalyzerTypes.BillsAccountSingleSubscribers]: {
        chartIdField: EExpensesAnalyzerField.MSISDN,
        isBillsType: true,
        isDynamicDisabled: true,
    },
    [EExpensesAnalyzerTypes.BillsUnpaidAccount]: {
        chartIdField: EExpensesAnalyzerField.ACCOUNT,
        isBillsType: true,
        isDynamicDisabled: true,
        isTableAccount: true,
    },
    [EExpensesAnalyzerTypes.BillsUnpaidAccountSingle]: {
        chartIdField: EExpensesAnalyzerField.ACCOUNT,
        isBillsType: true,
        isDynamicDisabled: true,
        isTableAccount: true,
    },
    [EExpensesAnalyzerTypes.BillsUnpaidAccountsSubscribers]: {
        chartIdField: EExpensesAnalyzerField.MSISDN,
        isBillsType: true,
        isDynamicDisabled: true,
    },
    [EExpensesAnalyzerTypes.BillsUnpaidAccountSingleSubscribers]: {
        chartIdField: EExpensesAnalyzerField.MSISDN,
        isBillsType: true,
        isDynamicDisabled: true,
    },
    [EExpensesAnalyzerTypes.SubscriberFixed]: {
        chartIdField: EExpensesAnalyzerField.MSISDN,
    },
    [EExpensesAnalyzerTypes.SubscriberFixedSingle]: {
        chartIdField: EExpensesAnalyzerField.MSISDN,
    },
    [EExpensesAnalyzerTypes.SubscriberMobile]: {
        chartIdField: EExpensesAnalyzerField.MSISDN,
    },
    [EExpensesAnalyzerTypes.SubscriberMobileSingle]: {
        chartIdField: EExpensesAnalyzerField.MSISDN,
    },
};

export const mapChartTypeToMinMonthCount: { [key in EExpensesAnalyzerChart]: number } = {
    [EExpensesAnalyzerChart.CATEGORY]: 1,
    [EExpensesAnalyzerChart.DYNAMIC]: 3,
};

export const mapTableNameToExpensesPageCookie: { [key: string]: string[] } = {
    [TableNameType.account]: [EExpensesAnalyzerTypes.Account],
    [TableNameType.bills]: [EExpensesAnalyzerTypes.BillsAccount, EExpensesAnalyzerTypes.BillsUnpaidAccount],
};

export const mapExpensesBillsTypeToSubscribersApi: { [key: string]: string } = {
    [EExpensesAnalyzerTypes.BillsAccount]: RoutePath.expensesAnalyzerBillsAccountsSubscribers,
    [EExpensesAnalyzerTypes.BillsAccountSingle]: RoutePath.expensesAnalyzerBillsAccountIdSubscribers,
    [EExpensesAnalyzerTypes.BillsUnpaidAccount]: RoutePath.expensesAnalyzerBillsUnpaidAccountsSubscribers,
    [EExpensesAnalyzerTypes.BillsUnpaidAccountSingle]: RoutePath.expensesAnalyzerBillsUnpaidAccountIdSubscribers,
};

export const mapExpensesTypeToCallsDetailRoutePath: { [key in EExpensesAnalyzerTypes]?: string } = {
    [EExpensesAnalyzerTypes.Account]: RoutePath.callsDetailExpensesAccounts,
    [EExpensesAnalyzerTypes.AccountSubscribers]: RoutePath.callsDetailExpensesAccountsSubscribers,
    [EExpensesAnalyzerTypes.AccountSingle]: RoutePath.callsDetailExpensesAccountId,
    [EExpensesAnalyzerTypes.AccountSingleSubscribers]: RoutePath.callsDetailExpensesAccountIdSubscribers,

    [EExpensesAnalyzerTypes.BillsAccount]: RoutePath.callsDetailExpensesBillsAccounts,
    [EExpensesAnalyzerTypes.BillsAccountsSubscribers]: RoutePath.callsDetailExpensesBillsAccountsSubscribers,
    [EExpensesAnalyzerTypes.BillsAccountSingle]: RoutePath.callsDetailExpensesBillsAccountId,
    [EExpensesAnalyzerTypes.BillsAccountSingleSubscribers]: RoutePath.callsDetailExpensesBillsAccountIdSubscribers,

    [EExpensesAnalyzerTypes.BillsUnpaidAccount]: RoutePath.callsDetailExpensesBillsUnpaidAccounts,
    [EExpensesAnalyzerTypes.BillsUnpaidAccountsSubscribers]:
    RoutePath.callsDetailExpensesBillsUnpaidAccountsSubscribers,
    [EExpensesAnalyzerTypes.BillsUnpaidAccountSingle]: RoutePath.callsDetailExpensesBillsUnpaidAccountId,
    [EExpensesAnalyzerTypes.BillsUnpaidAccountSingleSubscribers]:
    RoutePath.callsDetailExpensesBillsUnpaidAccountIdSubscribers,

    [EExpensesAnalyzerTypes.SubscriberFixed]: RoutePath.callsDetailExpensesSubscriberFixed,
    [EExpensesAnalyzerTypes.SubscriberMobile]: RoutePath.callsDetailExpensesSubscriberMobile,
};

export interface IExpensesAnalyzerOnboarding {
    chart: EOnboardingComponentsId;
    chartTab: EOnboardingComponentsId;
    table: EOnboardingComponentsId;
    tableFooterButtonCallDetails: EOnboardingComponentsId;
    tableFooterButtonShowDailyExpenses: EOnboardingComponentsId;
    tableFooterButtonShowSubsExpenses: EOnboardingComponentsId;
    tableFooterButtonWizardExpensesDetail: EOnboardingComponentsId;
}

const onboardingForAllAccounts: IExpensesAnalyzerOnboarding = {
    chart: EOnboardingComponentsId.expensesAnalyzerAccountChart,
    chartTab: EOnboardingComponentsId.expensesAnalyzerAccountChartTab,
    table: EOnboardingComponentsId.expensesAnalyzerAccountTable,
    tableFooterButtonCallDetails: EOnboardingComponentsId.expensesAnalyzerAccountTableFooterButtonCallDetails,
    tableFooterButtonShowDailyExpenses: EOnboardingComponentsId
        .expensesAnalyzerAccountTableFooterButtonShowDailyExpenses,
    tableFooterButtonShowSubsExpenses: EOnboardingComponentsId.expensesAnalyzerAccountTableFooterButtonShowSubsExpenses,
    tableFooterButtonWizardExpensesDetail: EOnboardingComponentsId
        .expensesAnalyzerAccountTableFooterButtonWizardExpensesDetail,
};

export const mapExpensesTypeToOnboarding: { [key in EExpensesAnalyzerTypes]?: IExpensesAnalyzerOnboarding } = {
    [EExpensesAnalyzerTypes.Account]: onboardingForAllAccounts,
    [EExpensesAnalyzerTypes.AccountSingle]: onboardingForAllAccounts,
    [EExpensesAnalyzerTypes.BillsAccount]: onboardingForAllAccounts,
    [EExpensesAnalyzerTypes.BillsAccountSingle]: onboardingForAllAccounts,
    [EExpensesAnalyzerTypes.BillsAccountsSubscribers]: onboardingForAllAccounts,
    [EExpensesAnalyzerTypes.BillsAccountSingleSubscribers]: onboardingForAllAccounts,
    [EExpensesAnalyzerTypes.BillsUnpaidAccount]: onboardingForAllAccounts,
    [EExpensesAnalyzerTypes.BillsUnpaidAccountSingle]: onboardingForAllAccounts,
    [EExpensesAnalyzerTypes.BillsUnpaidAccountsSubscribers]: onboardingForAllAccounts,
    [EExpensesAnalyzerTypes.BillsUnpaidAccountSingleSubscribers]: onboardingForAllAccounts,
};
